import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { connect, Dispatch } from 'react-redux';
import { DsArea, WageAndTravelViewType, WageAndTravelStatus, WageAndTravelCategory, WageAndTravelGroup } from '@nkr/enums/index';
import { IWatrRequest } from '@nkr/models/index';
import { WageAndTravelReimbursementRequestStatus } from '@nkr/enums/watr-reimbursement-request-status';
import { DropdownButton, MenuItem } from 'react-bootstrap';
import { WatrAction } from './watr-action';
import { hasPendingTravelApprovalForWatr, isIntlStandardDerReimburseTravel, isIntlStandardDerVerify, readAdvancedProgressForBuckets, isIntlBlockedFromNkrTodo, isIntlWorkupReimbursementProgram, readIsInternationallyEligibleForWatr, usesIntlStandardDerAdminActions } from './advanced-reimbursement-status-buckets';
import { isAdvancedDsArea } from './watr-nav-area';
import { AdvancedWorkflowStatus } from '@nkr/enums/index';
import OverlayMenuButton from '@nkr/components/overlay-menu-button';
import { AppActionCreators, IApplicationState } from '@nkr/store/index';

function intlUsesStandardDerHandoff(request: IWatrRequest): boolean {
    if (!readIsInternationallyEligibleForWatr(request)) {
        return false;
    }
    const travels = request.requestTravels || [];
    return travels.some(x =>
        x.status === WageAndTravelReimbursementRequestStatus.PendingApproval
        || x.status === WageAndTravelReimbursementRequestStatus.AutoApproved
        || x.status === WageAndTravelReimbursementRequestStatus.CenterApproved
        || x.status === WageAndTravelReimbursementRequestStatus.AdminApproved);
}

const menuItemList = [
    { action: WatrAction.Edit, title: <>Edit</> },
    { action: WatrAction.ResendInvitation, title: <>Re-Send Invitation</> },
    { action: WatrAction.CompleteForDonor, title: <>Complete for Donor</> },
    { action: WatrAction.UploadTravelDetails, title: <>Upload Travel Details for Donor</> },
    { action: WatrAction.ConfirmDonation, title: <>Confirm Donation</> },
    { action: WatrAction.ApproveWeekReimbursements, title: <>Approve Wage Reimbursements</> },
    { action: WatrAction.RequestAddlDays, title: <>Request Additional Wage Reimbursement</> },
    { action: WatrAction.Audit, title: <>Audit</> },
    { action: WatrAction.TravelDetails, title: <>Travel Details</> },
    { action: WatrAction.ReimburseWages, title: <>Reimburse Wages</> },
    { action: WatrAction.ReimburseTravel, title: <>Reimburse Expenses</> },
    { action: WatrAction.VerifyWages, title: <>Verify Wages</> },
    { action: WatrAction.VerifyTravel, title: <>Verify Expenses</> },
    { action: WatrAction.CancelRequest, title: <>Cancel Request</> },
    { action: WatrAction.SetPaymentError, title: <>Move to Payment Error</> },
    { action: WatrAction.ResolvePaymentError, title: <>Resolve Payment Error</> },
    { action: WatrAction.RequestDonorReview, title: <>Request Donor Review</> },
    { action: WatrAction.ApproveAdvancedReimbursement, title: <>Approve Card</> },
    { action: WatrAction.DenyAdvancedReimbursement, title: <>Decline Card</> },
    { action: WatrAction.HoldAdvancedReimbursement, title: <>Hold</> },
    { action: WatrAction.ReleaseAdvancedReimbursement, title: <>Release</> },
    { action: WatrAction.ReconcileTransactions, title: <>Reconcile Transactions</> },
    { action: WatrAction.ActivateAdvancedReimbursement, title: <>Activate Card</> },
    { action: -1, title: null }
];

interface IOwnProps {
    request: IWatrRequest,
    isNkrSuper: boolean,
    showBsMenu: boolean,
    area: DsArea,
    category: WageAndTravelCategory,
    updateSidebarView: (id: number, type: WageAndTravelViewType) => void,
    updateModalView: (id: number, type: WageAndTravelViewType) => void,
    onMenuToggle: (show: boolean) => void,
    onItemSelected: (req: number, action: number) => void
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void
}

type IProps = IOwnProps & IDispatchProps;

class ActionsDropdown extends React.Component<IProps, {}> {
    private donorShieldNavigationInFlight = false;

    isPopup = () => {
        return true;
    }

    getUrl = (action: WatrAction, requestId: number) => {
        switch (action) {
            case WatrAction.UploadTravelDetails:
            case WatrAction.CompleteForDonor:
                return null;
        }
    }

    handleDonorShieldNavigation = async (watrRequestId: number, destinationPath: string = 'dashboard') => {
        if (!watrRequestId || this.donorShieldNavigationInFlight) {
            return;
        }

        this.donorShieldNavigationInFlight = true;
        await this.props.showLoading('Generating access link...');

        const {
            generateDashboardUrl,
            appendAdminContextToDonorShieldUrl,
            hasValidAdminAuthContext,
            donorShieldUrlHasAdminContext,
            getAdminAuthContext,
            DONOR_SHIELD_OPEN_ERROR_USER_MESSAGE,
            logDonorShieldOpenFailure
        } = await import('../../../../services/donor-shield-api');

        try {
            if (!hasValidAdminAuthContext()) {
                const { toast } = await import('react-toastify');
                logDonorShieldOpenFailure('invalid admin auth context before generate-url', getAdminAuthContext());
                toast.error(DONOR_SHIELD_OPEN_ERROR_USER_MESSAGE);
                return;
            }

            const response = await generateDashboardUrl(watrRequestId, destinationPath);

            if (response.success && response.data?.dashboardUrl) {
                let dashboardUrl = response.data.dashboardUrl;
                
                if (response.data.oneTimeToken) {
                    const url = new URL(dashboardUrl);
                    url.searchParams.set('otp', response.data.oneTimeToken);
                    dashboardUrl = url.toString();
                }

                try {
                    dashboardUrl = appendAdminContextToDonorShieldUrl(dashboardUrl);
                } catch (appendError) {
                    const { toast } = await import('react-toastify');
                    logDonorShieldOpenFailure('appendAdminContextToDonorShieldUrl failed', appendError);
                    toast.error(DONOR_SHIELD_OPEN_ERROR_USER_MESSAGE);
                    return;
                }

                if (!donorShieldUrlHasAdminContext(dashboardUrl)) {
                    const { toast } = await import('react-toastify');
                    logDonorShieldOpenFailure('dashboard URL missing admin query params', { dashboardUrl });
                    toast.error(DONOR_SHIELD_OPEN_ERROR_USER_MESSAGE);
                    return;
                }
                
                window.open(dashboardUrl, '_blank');
            } else {
                const { toast } = await import('react-toastify');
                logDonorShieldOpenFailure('generate-url unsuccessful', response);
                toast.error(DONOR_SHIELD_OPEN_ERROR_USER_MESSAGE);
            }
        } catch (error) {
            const { toast } = await import('react-toastify');
            logDonorShieldOpenFailure('unexpected error during DonorShield open', error);
            toast.error(DONOR_SHIELD_OPEN_ERROR_USER_MESSAGE);
        } finally {
            this.donorShieldNavigationInFlight = false;
            this.props.hideLoading();
        }
    }

    handleMenuToggle = (show: boolean) => {
        this.props.onMenuToggle(show);
    };

    handleMenuSelection = (id: number, action: number) => {
        this.props.onItemSelected(id, action);
    };

    renderBsStyle = () => {
        let req = this.props.request;
        let requestId = req.id;
        let donationDate = moment(req.donationDate);
        let showResendInvite = this.props.request.status <= WageAndTravelStatus.PendingDocuments
            || this.props.request.status == WageAndTravelStatus.Donated

        let showConfirmDonation =
            !this.props.isNkrSuper
            && req.status < WageAndTravelStatus.Denied
            && (req.afterDonationDate)
            && !req.donationConfirmed
            && req.donorShieldId > 0

        let showTravelDetails = req.requestTravels
            .filter(x =>
                (x.status > WageAndTravelReimbursementRequestStatus.PendingApproval && x.status < WageAndTravelReimbursementRequestStatus.CenterDeclined)
                || x.status == WageAndTravelReimbursementRequestStatus.Reimbursed).length > 0
            && this.props.isNkrSuper
            && req.donationConfirmed;

        let showApproveWeekReimbursements = req.requestWeeks.filter(x => x.status == WageAndTravelReimbursementRequestStatus.PendingApproval).length > 0
            && req.donationConfirmed
            && !this.props.isNkrSuper;

        let showReimburseTravel =
            this.props.isNkrSuper
            && (this.props.category == WageAndTravelCategory.PendingPayment || this.props.category == WageAndTravelCategory.PendingWire)
            && (req.requestTravels.filter(x => x.status == WageAndTravelReimbursementRequestStatus.AdminApproved).length > 0)
            && (
                isIntlStandardDerReimburseTravel(req, this.props.category)
                || req.donationConfirmed
            );

        let showVerifyTravel =
            this.props.isNkrSuper
            && hasPendingTravelApprovalForWatr(req)
            && (
                (this.props.category == WageAndTravelCategory.PendingDERVerify && req.donationConfirmed)
                || (isIntlStandardDerVerify(req) && !isAdvancedDsArea(this.props.area))
            );

        let showReimburseWages =
            this.props.isNkrSuper
            && req.donationConfirmed
            && (this.props.category == WageAndTravelCategory.PendingPayment || this.props.category == WageAndTravelCategory.PendingWire)
            && req.status > WageAndTravelStatus.PendingNkrVerifyAmount
            && req.status < WageAndTravelStatus.Denied
            && req.requestWeeks
                .filter(x => x.status > WageAndTravelReimbursementRequestStatus.PendingApproval
                    && x.status < WageAndTravelReimbursementRequestStatus.CenterDeclined).length > 0;

        let showVerifyWages =
            this.props.isNkrSuper
            && this.props.category == WageAndTravelCategory.PendingLWRVerify
            && req.status >= WageAndTravelStatus.PendingNkrVerifyTax
            && req.status <= WageAndTravelStatus.PendingNkrVerifyAmount;


        return (
            <>
                <DropdownButton id={`action-menu-${this.props.request}`} bsStyle='primary' bsSize='sm' title='Actions' block pullRight>
                    <MenuItem target='_blank' onClick={() => this.props.updateSidebarView(this.props.request.id, WageAndTravelViewType.RequestData)}>
                        <span className='glyphicon glyphicon-edit'></span> Edit
                    </MenuItem>

                    {this.props.isNkrSuper &&
                        <MenuItem onClick={() => this.props.updateSidebarView(this.props.request.id, WageAndTravelViewType.ReimburseWageAudit)}>
                            <span className='glyphicon glyphicon-zoom-in'></span> Audit
                        </MenuItem>
                    }

                    {showResendInvite &&
                        <MenuItem>
                            <span className='glyphicon glyphicon-edit'></span> Re-Send Invitation
                        </MenuItem>
                    }

                    {this.props.request.status <= WageAndTravelStatus.PendingDocuments &&
                        <MenuItem onClick={() => this.handleDonorShieldNavigation(req.id)}>
                            <span className='glyphicon glyphicon-upload'></span> Complete for Donor
                        </MenuItem>
                    }

                    {this.props.request.status == WageAndTravelStatus.Donated &&
                        <MenuItem onClick={() => this.handleDonorShieldNavigation(req.id)}>
                            <span className='glyphicon glyphicon-star'></span> Upload Travel Details for Donor
                        </MenuItem>
                    }

                    {showApproveWeekReimbursements &&
                        <MenuItem onClick={() => this.props.updateSidebarView(this.props.request.id, WageAndTravelViewType.ApproveWeekReimbursements)}>
                            <span className='glyphicon glyphicon-edit'></span> Approve Week Wage Reimbursements
                        </MenuItem>
                    }

                    {showConfirmDonation &&
                        <MenuItem onClick={() => this.props.updateSidebarView(this.props.request.id, WageAndTravelViewType.ConfirmDonation)}>
                            <span className='glyphicon glyphicon-edit'></span> Confirm Donation
                        </MenuItem>
                    }

                    {/* {req.canRequestAdditionalLwrDays &&
                        <MenuItem onClick={() => this.props.updateSidebarView(this.props.request.id, WageAndTravelViewType.AdditionalDays)}>
                            <span className='glyphicon glyphicon-edit'></span> Request Additional Wage Reimbursement
                        </MenuItem>
                    } */}

                    {showTravelDetails &&
                        <MenuItem onClick={() => this.props.updateSidebarView(this.props.request.id, WageAndTravelViewType.ReimburseTravel)}>
                            <span className='glyphicon glyphicon-zoom-in'></span> Travel Details
                        </MenuItem>
                    }

                    {showReimburseWages &&
                        <MenuItem onClick={() => this.props.updateSidebarView(this.props.request.id, WageAndTravelViewType.ReimburseWage)}>
                            <span className='glyphicon glyphicon-zoom-in'></span> Reimburse Wages
                        </MenuItem>
                    }

                    {showVerifyWages &&
                        <MenuItem onClick={() => this.props.updateSidebarView(this.props.request.id, WageAndTravelViewType.VerifyWage)}>
                            <span className='glyphicon glyphicon-zoom-in'></span> Verify Wages
                        </MenuItem>
                    }

                    {showReimburseTravel &&
                        <MenuItem onClick={() => this.props.updateSidebarView(this.props.request.id, WageAndTravelViewType.ReimburseTravel)}>
                            <span className='glyphicon glyphicon-zoom-in'></span> Reimburse Expenses
                        </MenuItem>
                    }

                    {showVerifyTravel &&
                        <MenuItem onClick={() => this.props.updateSidebarView(this.props.request.id, WageAndTravelViewType.VerifyTravel)}>
                            <span className='glyphicon glyphicon-zoom-in'></span> Verify Expenses
                        </MenuItem>
                    }


                    <MenuItem target='_blank' onClick={() => this.props.updateModalView(this.props.request.id, WageAndTravelViewType.CancelRequest)}>
                        <span className='glyphicon glyphicon-remove'></span> Cancel Request
                    </MenuItem>
                </DropdownButton>
            </>
        );
    }

    getMenuItems = () => {
        let menuItems: WatrAction[] = [];

        let { status, afterDonationDate, donationConfirmed, donorShieldId, requestTravels, requestWeeks } = this.props.request;

        let showResendInvite = this.props.request.status <= WageAndTravelStatus.PendingDocuments
            || this.props.request.status == WageAndTravelStatus.Donated

        let showConfirmDonation =
            status < WageAndTravelStatus.Denied
            && afterDonationDate
            && !donationConfirmed
            && donorShieldId > 0
            && !this.props.isNkrSuper;;

        let showTravelDetails = requestTravels
            .filter(x =>
                (x.status > WageAndTravelReimbursementRequestStatus.PendingApproval && x.status < WageAndTravelReimbursementRequestStatus.CenterDeclined)
                || x.status == WageAndTravelReimbursementRequestStatus.Reimbursed).length > 0
            && this.props.isNkrSuper
            && donationConfirmed;

        let showApproveWeekReimbursements = requestWeeks.filter(x => x.status == WageAndTravelReimbursementRequestStatus.PendingApproval && x.weekRequested > 3).length > 0
            && !this.props.isNkrSuper;

        let showReimburseTravel =
            this.props.isNkrSuper
            && (this.props.category == WageAndTravelCategory.PendingPayment || this.props.category == WageAndTravelCategory.PendingWire)
            && (requestTravels.filter(x => x.status == WageAndTravelReimbursementRequestStatus.AdminApproved).length > 0)
            && (
                isIntlStandardDerReimburseTravel(this.props.request, this.props.category)
                || donationConfirmed
            );

        let showVerifyTravel =
            this.props.isNkrSuper
            && hasPendingTravelApprovalForWatr(this.props.request)
            && (
                (this.props.category == WageAndTravelCategory.PendingDERVerify && donationConfirmed)
                || (isIntlStandardDerVerify(this.props.request) && !isAdvancedDsArea(this.props.area))
            );

        let showReimburseWages =
            this.props.isNkrSuper
            && donationConfirmed
            && (this.props.category == WageAndTravelCategory.PendingPayment || this.props.category == WageAndTravelCategory.PendingWire)
            && status > WageAndTravelStatus.PendingNkrVerifyAmount
            && status < WageAndTravelStatus.Denied
            && requestWeeks
                .filter(x => x.status > WageAndTravelReimbursementRequestStatus.PendingApproval
                    && x.status < WageAndTravelReimbursementRequestStatus.CenterDeclined).length > 0;

        let showVerifyWages =
            this.props.isNkrSuper
            && this.props.category == WageAndTravelCategory.PendingLWRVerify
            && status >= WageAndTravelStatus.PendingNkrVerifyTax
            && status <= WageAndTravelStatus.PendingNkrVerifyAmount;

        const isPaymentErrorCategory = this.props.category == WageAndTravelCategory.PaymentError;

        menuItems.push(WatrAction.Edit);

        if (this.props.isNkrSuper) {
            menuItems.push(WatrAction.Audit);
        }

        if (showResendInvite) {
            menuItems.push(WatrAction.ResendInvitation);
        }

        if (this.props.request.status <= WageAndTravelStatus.PendingDocuments) {
            menuItems.push(WatrAction.CompleteForDonor);
        }

        if (this.props.request.status == WageAndTravelStatus.Donated) {
            menuItems.push(WatrAction.UploadTravelDetails);
        }

        if (showApproveWeekReimbursements) {
            menuItems.push(WatrAction.ApproveWeekReimbursements);
        }

        if (showConfirmDonation) {
            menuItems.push(WatrAction.ConfirmDonation);
        }

        if (showTravelDetails) {
            menuItems.push(WatrAction.TravelDetails);
        }

        if (showReimburseWages) {
            menuItems.push(WatrAction.ReimburseWages)
        }

        if (showVerifyWages) {
            menuItems.push(WatrAction.VerifyWages)
        }

        if (showVerifyTravel) {
            menuItems.push(WatrAction.VerifyTravel)
        }

        if (showReimburseTravel) {
            menuItems.push(WatrAction.ReimburseTravel)
        }

        if (isPaymentErrorCategory) {
            menuItems.push(WatrAction.RequestDonorReview);
        }

        if(this.props.category == WageAndTravelCategory.PendingPayment 
            || this.props.category == WageAndTravelCategory.PendingWire 
            || (this.props.isNkrSuper && this.props.category == WageAndTravelCategory.Search)
        ) {
            menuItems.push(WatrAction.SetPaymentError)
        }
        
        if(this.props.category == WageAndTravelCategory.PaymentError) {
            menuItems.push(WatrAction.ResolvePaymentError)
        }

        if (this.props.isNkrSuper && isAdvancedDsArea(this.props.area) && !usesIntlStandardDerAdminActions(this.props.request)) {
            let advancedProgress = readAdvancedProgressForBuckets(this.props.request);
            let advancedStatusCode = advancedProgress != null ? advancedProgress.statusCode : undefined;
            let onTrack = advancedProgress?.onTrack === true;
            let invitePending = advancedProgress?.invitePending === true;

            let advancedActions: WatrAction[] = [];
            if (!invitePending && onTrack && advancedStatusCode === AdvancedWorkflowStatus.AwaitingDonorProfile) {
                advancedActions = [];
            } else if (!invitePending && onTrack && advancedStatusCode === AdvancedWorkflowStatus.AwaitingNkrReview) {
                advancedActions = isIntlWorkupReimbursementProgram(this.props.request)
                    || isIntlBlockedFromNkrTodo(this.props.request, advancedProgress)
                    ? []
                    : [
                        WatrAction.ApproveAdvancedReimbursement,
                        WatrAction.DenyAdvancedReimbursement,
                        WatrAction.HoldAdvancedReimbursement,
                    ];
            } else if (!invitePending && onTrack && advancedStatusCode === AdvancedWorkflowStatus.AwaitingDonorSubmission) {
                const hasPendingTravel = (this.props.request.requestTravels || []).some(
                    x => x.status === WageAndTravelReimbursementRequestStatus.PendingApproval
                );
                if (isIntlWorkupReimbursementProgram(this.props.request)
                    || intlUsesStandardDerHandoff(this.props.request)) {
                    advancedActions = [WatrAction.HoldAdvancedReimbursement];
                } else if (hasPendingTravel) {
                    advancedActions = [
                        WatrAction.VerifyTravel,
                        WatrAction.HoldAdvancedReimbursement,
                    ];
                } else {
                    advancedActions = [
                        WatrAction.ReconcileTransactions,
                        WatrAction.HoldAdvancedReimbursement,
                    ];
                }
            } else if (!invitePending && onTrack && advancedStatusCode === AdvancedWorkflowStatus.PendingNkrVerification) {
                advancedActions = isIntlWorkupReimbursementProgram(this.props.request)
                    || intlUsesStandardDerHandoff(this.props.request)
                    ? [WatrAction.HoldAdvancedReimbursement]
                    : [
                        WatrAction.VerifyTravel,
                        WatrAction.HoldAdvancedReimbursement,
                    ];
            } else if (!invitePending && onTrack && advancedStatusCode === AdvancedWorkflowStatus.ReadyToReconcile) {
                advancedActions = isIntlWorkupReimbursementProgram(this.props.request)
                    || intlUsesStandardDerHandoff(this.props.request)
                    ? [WatrAction.HoldAdvancedReimbursement]
                    : [
                        WatrAction.ReconcileTransactions,
                        WatrAction.HoldAdvancedReimbursement,
                    ];
            } else if (!invitePending && onTrack && advancedStatusCode === AdvancedWorkflowStatus.OnHold) {
                advancedActions = [WatrAction.ReleaseAdvancedReimbursement];
            } else if (!invitePending && onTrack && advancedStatusCode === AdvancedWorkflowStatus.Complete) {
                advancedActions = [WatrAction.ActivateAdvancedReimbursement];
            }

            if (advancedActions.length > 0) {
                menuItems.push(-1);
                menuItems.push(...advancedActions);
            }
        }

        menuItems.push(-1);
        menuItems.push(WatrAction.CancelRequest);

        return menuItems;
    }
    render = () => {
        if (this.props.showBsMenu) {
            return this.renderBsStyle();
        }

        const menuItems = this.getMenuItems().map(item => {
            return menuItemList.find(x => x.action == item);
        });

        const menu = menuItems.map(item => {
            let url = this.getUrl(item.action, this.props.request.id);
            let id = item.action >= 0 ? this.props.request.id : 'div-' + this.props.request.id + '-' + Math.random() * 10000;

            if (url == null) {
                if (item.action >= 0) {
                    return { id, action: item.action, include: true, selectable: true, element: <>{item.title}</> };
                } else {
                    return { id, action: item.action, include: true, selectable: false, divider: true, element: null };
                }
            } else {
                return { id, action: item.action, include: true, selectable: false, element: <a href={url.link} target={url.target}>{item.title}</a> };
            }
        });

        return (
            <OverlayMenuButton
                menu={menu}
                bsStyle='primary'
                bsSize='xs'
                title='Actions'
                listItemStyle={{ padding: '2px 10px', fontSize: 14 }}
                includeCaret={true}
                onToggle={this.handleMenuToggle}
                onSelect={(id, action) => {
                    if (action === WatrAction.UploadTravelDetails || action === WatrAction.CompleteForDonor) {
                        this.handleDonorShieldNavigation(this.props.request.id);
                    } else {
                        this.handleMenuSelection(id, action);
                    }
                }}
            />
        );
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>) => {
    return {
        showLoading: (message?: string) => {
            return dispatch(AppActionCreators.showLoadingAsync(message || 'Loading...'));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        }
    };
};

export default connect<{}, IDispatchProps, IOwnProps>(null, mapDispatchToProps)(ActionsDropdown);